/** MemsoPicksStrip — W070.7 curate-not-aggregate consumer, W071.1
 *  bidirectional learning loop.
 *
 *  An ADDITIVE "Memso picks" strip rendered ABOVE a surface's full
 *  aggregate list. It fetches the surface's /curate endpoint, renders
 *  the few items the curator chose (each annotated with the pick's
 *  one-sentence reason), and otherwise renders NOTHING. The page's
 *  full list below is always the fallback, so a curator that is empty,
 *  slow, or down can never produce a regression or an empty-state
 *  surprise — the strip just silently collapses.
 *
 *  Memso's promise is "curate, not aggregate": the LLM picks the few
 *  that matter. This strip is how that curation finally reaches the
 *  user on every surface, not just Today.
 *
 *  W071.1 closes the loop. Until now the strip was display-only, so
 *  the single most valuable signal — "did the user act on what Memso
 *  *picked*?" — was dropped on the floor. Now each picked row emits an
 *  attention event under the surface's catalog key (mirroring how
 *  TodayClusters emits open/dismiss), and the optional dismiss
 *  affordance fires the W010/W011 sender suppression + shows the trust
 *  microcopy inline. The learner can finally weigh pick-vs-act.
 *
 *  Generic over the surface's row type. Each page supplies:
 *    - surface:  the surface's catalog key (engagements, awaiting_you,
 *                recommendations, observations, library, trip, people)
 *                so attention attributes to the same key the surface's
 *                full list and the scorecard already read.
 *    - load():   a function returning { picks, items } for the surface
 *    - children: a render prop that draws one picked row + its reason
 *    - onOpen?:  invoked when the user opens a picked row (the strip
 *                emits an `open_source` attention event regardless).
 *    - itemKindOf?: maps a row to its attention item_kind.
 *    - suppressOf?: an optional accessor that, where the surface has a
 *                sender (engagements / awaiting / recommendations /
 *                observations), performs the sender suppression and
 *                resolves to the server's trust message. When omitted
 *                (people / trip / library: no sender) the dismiss
 *                button still emits a `dismiss` attention event but
 *                skips the suppression POST + microcopy.                 */

import { useCallback, useEffect, useState } from "react";
import { Sparkles, X as XIcon } from "lucide-react";
import type { CuratePick } from "../lib/api";
import { useAttention } from "../lib/useAttention";

interface PickedEntry<T> {
  pick: CuratePick;
  item: T;
}

/** Result of a per-row sender suppression. The server returns a
 *  ready-made trust message (W010 threshold copy / W011 hide-forever
 *  copy); we surface it verbatim so the strip never invents copy. */
export interface PickSuppressResult {
  trust_message?: string | null;
}

export function MemsoPicksStrip<T extends { id?: string }>({
  surface,
  title = "Memso picks",
  subtitle = "What matters now, picked by Memso.",
  load,
  children,
  className,
  itemKindOf,
  onOpen,
  suppressOf,
}: {
  /** Surface's catalog key. Drives the attention ledger segmentation
   *  so pick interactions attribute to the same key the surface's full
   *  list emits and the curation scorecard reads. Required. */
  surface: string;
  /** Section label. Defaults to "Memso picks". */
  title?: string;
  /** One-line hint under the label. */
  subtitle?: string;
  /** Fetch the surface's /curate result. Resolves to picks plus the
   *  entity rows the curator chose (already filtered + ordered). */
  load: () => Promise<{ picks: CuratePick[]; items: T[] }>;
  /** Render one picked item with its curation reason. */
  children: (item: T, reason: string, rank: number) => React.ReactNode;
  className?: string;
  /** Attention item_kind for a row. Defaults to "item". */
  itemKindOf?: (item: T) => string;
  /** Page-side open handler (e.g. navigate). The strip emits an
   *  `open_source` attention event whether or not this is supplied. */
  onOpen?: (item: T) => void;
  /** Optional per-row sender suppression. Present only on surfaces
   *  whose items have a sender. When present the dismiss button POSTs
   *  the suppression and shows the returned trust message inline; when
   *  absent the dismiss button is emit-only (a `dismiss` attention
   *  event, no POST, no microcopy). */
  suppressOf?: (item: T) => Promise<PickSuppressResult | void>;
}) {
  const [entries, setEntries] = useState<PickedEntry<T>[] | null>(null);
  // Rows the user dismissed this session: hidden locally and never
  // re-rendered until the next load. Additive: the full list below is
  // unaffected.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  // Inline trust microcopy keyed by row id, shown after a suppression.
  const [trust, setTrust] = useState<Record<string, string>>({});
  const { emit } = useAttention(surface);

  const kindOf = useCallback(
    (item: T) => (itemKindOf ? itemKindOf(item) : "item"),
    [itemKindOf],
  );

  useEffect(() => {
    let cancelled = false;
    load()
      .then((res) => {
        if (cancelled) return;
        const picks = Array.isArray(res?.picks) ? res.picks : [];
        const items = Array.isArray(res?.items) ? res.items : [];
        if (picks.length === 0 || items.length === 0) {
          setEntries([]);
          return;
        }
        const byId = new Map<string, T>();
        for (const it of items) {
          if (it && typeof it.id === "string") byId.set(it.id, it);
        }
        const mapped: PickedEntry<T>[] = picks
          .slice()
          .sort((a, b) => a.rank - b.rank)
          .flatMap((p) => {
            const item = byId.get(p.id);
            return item ? [{ pick: p, item }] : [];
          });
        setEntries(mapped);
      })
      .catch(() => {
        // Curator empty / down / errored: collapse silently. The full
        // aggregate list below is always the fallback.
        if (!cancelled) setEntries([]);
      });
    return () => {
      cancelled = true;
    };
    // load is expected to be stable (defined inline per render is fine
    // for a one-shot fetch on mount); intentionally run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open emission is opt-in via onOpen so we never double-count with a
  // child card that already emits its own open_source (e.g.
  // EngagementCard). Pages whose card does NOT self-emit pass onOpen to
  // get strip-level open attribution under the surface key; pages whose
  // card self-emits omit it. Either way the strip owns the dismiss
  // signal, which is the new W071.1 learning channel.
  const handleOpen = useCallback(
    (item: T) => {
      if (!onOpen) return;
      if (typeof item.id === "string") {
        void emit("open_source", {
          cluster: "memso_picks",
          itemKind: kindOf(item),
          itemId: item.id,
        });
      }
      onOpen(item);
    },
    [emit, kindOf, onOpen],
  );

  const handleDismiss = useCallback(
    async (item: T) => {
      const id = item.id;
      if (typeof id !== "string") return;
      // Hide locally immediately; the gesture must feel instant.
      setDismissed((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      // Emit-only by default: the learner always sees the dismiss.
      void emit("dismiss", {
        cluster: "memso_picks",
        itemKind: kindOf(item),
        itemId: id,
      });
      // Surfaces with a sender additionally POST the suppression and
      // surface the server's trust microcopy verbatim.
      if (suppressOf) {
        try {
          const res = await suppressOf(item);
          const msg = res?.trust_message;
          if (msg) setTrust((prev) => ({ ...prev, [id]: msg }));
        } catch {
          /* best-effort: the next poll reflects the suppression */
        }
      }
    },
    [emit, kindOf, suppressOf],
  );

  // Nothing curated (or still loading): render nothing. Additive only.
  if (!entries || entries.length === 0) return null;

  const visible = entries.filter(({ item }) => {
    const id = item.id;
    return typeof id !== "string" || !dismissed.has(id);
  });
  if (visible.length === 0) return null;

  return (
    <section
      className={
        "rounded-xl border border-violet-200/70 dark:border-violet-800/40 bg-violet-50/40 dark:bg-violet-950/20 p-3 sm:p-4 " +
        (className ?? "")
      }
      aria-label={title}
    >
      <header className="mb-2 flex items-center gap-2 text-violet-700 dark:text-violet-300">
        <Sparkles className="h-4 w-4" aria-hidden />
        <h2 className="text-sm font-semibold uppercase tracking-wide">{title}</h2>
      </header>
      <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
      <div className="space-y-2">
        {visible.map(({ pick, item }) => {
          const id = item.id as string;
          const trustMsg = id ? trust[id] : undefined;
          return (
            <div key={pick.id}>
              <div className="flex items-start gap-2">
                <div
                  className="min-w-0 flex-1"
                  onClickCapture={() => handleOpen(item)}
                >
                  {children(item, pick.reason, pick.rank)}
                </div>
                <button
                  type="button"
                  onClick={() => void handleDismiss(item)}
                  aria-label="Dismiss this pick"
                  title="Dismiss"
                  className="mt-0.5 inline-flex flex-shrink-0 items-center rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-1.5 py-1.5 hover:bg-rose-100 dark:hover:bg-rose-900/30 hover:text-rose-700 dark:hover:text-rose-300"
                >
                  <XIcon className="h-3.5 w-3.5" />
                </button>
              </div>
              {pick.reason ? (
                <p className="mt-1 pl-1 text-[11px] italic text-violet-700/80 dark:text-violet-300/80">
                  {pick.reason}
                </p>
              ) : null}
              {trustMsg ? (
                <p
                  role="status"
                  aria-live="polite"
                  className="mt-1 pl-1 text-[11px] text-emerald-700 dark:text-emerald-300"
                >
                  {trustMsg}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
